'use client';

import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Link as MuiLink,
} from '@mui/material';
import Link from 'next/link';
import { formatDistanceToNow } from 'date-fns';
import { JobStatus, JobType } from '@prisma/client';

interface Job {
  id: string;
  title: string;
  status: JobStatus;
  type: JobType;
  createdAt: Date;
}

interface JobsSectionProps {
  postedJobs: Job[];
  acceptedJobs: Job[];
  userId: string;
}

export default function JobsSection({ postedJobs, acceptedJobs, userId }: JobsSectionProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [visibleJobs, setVisibleJobs] = useState(3);

  const currentJobs = activeTab === 0 ? postedJobs : acceptedJobs;

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case 'OPEN':
        return 'info';
      case 'IN_PROGRESS':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Jobs
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_: React.SyntheticEvent, newValue: number) => setActiveTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label={`Posted (${postedJobs.length})`} />
        <Tab label={`Fulfilled (${acceptedJobs.length})`} />
      </Tabs>

      {currentJobs.length > 0 ? (
        <>
          <List disablePadding>
            {currentJobs.slice(0, visibleJobs).map(job => (
              <ListItem
                key={job.id}
                component={Link}
                href={`/jobs/${job.id}`}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={job.title}
                  secondary={formatDistanceToNow(job.createdAt, {
                    addSuffix: true,
                  })}
                  sx={{ flex: 1 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                  }}
                >
                  <Chip label={job.type} size="small" sx={{ textTransform: 'capitalize' }} />
                  <Chip
                    label={job.status.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(job.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>

          {currentJobs.length > visibleJobs && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button variant="outlined" onClick={() => setVisibleJobs(prev => prev + 3)}>
                Show More
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Typography color="text.secondary" align="center" py={4}>
          {activeTab === 0 ? 'No jobs posted yet' : 'No jobs fulfilled yet'}
          {activeTab === 0 && (
            <>
              <br />
              <MuiLink component={Link} href="/jobs/create">
                Post your first job
              </MuiLink>
            </>
          )}
        </Typography>
      )}
    </Box>
  );
}
